<template>
  <div class="product-column">
    <div class="icon-name-wrapper">
      <div v-if="showWarning(sub) || showDanger(sub)">
        <svg-icon-component v-if="showWarning(sub)" icon="circle-triangle" class="warning icon-sm mr-quarter" />
        <svg-icon-component v-if="showDanger(sub)" icon="circle-exclamation" class="danger icon-sm mr-quarter" />
      </div>
      <div>
        <ul>
          <li>{{ sub.productDisplayName }}</li>
          <li v-if="isOnlyKeycode() && sub.displayKeycode.length === 1">
            {{ t("SubscriptionInformationComponent.keycode") }} {{ sub.displayKeycode[0] }}
          </li>
          <li v-if="isOnlyKeycode() && sub.displayKeycode.length > 1">
            {{ t("SubscriptionInformationComponent.keycode") }}
            <ul>
              <li v-for="item in sub.displayKeycode" :key="item.valueOf()">{{ item }}</li>
            </ul>
          </li>
          <li v-else-if="computerName()" class="word-break">
            {{ t("SubscriptionInformationComponent.computer") }} {{ computerName() }}
          </li>
          <li v-if="showSafeUpgradeLink() && !isProcessingRenewal(sub)">
            <button type="button" class="inline-btn-link" @click="upgradeSafeSubscription(sub)">
              <span v-if="showBuyFlow()">{{ t("SubscriptionInformationComponent.changeSubscription") }}</span>
              <span v-else>{{ t("SubscriptionInformationComponent.upgradeLink") }}</span>
            </button>
          </li>
          <li v-if="showWebrootUpgradeLink()">
            <button type="button" class="inline-btn-link" @click="upgradeWebrootSubscription()">
              {{ t("SubscriptionInformationComponent.upgradeLink") }}
            </button>
          </li>
        </ul>
        <ul v-if="(isSafePlusOrPrime() || showKeycodeForTrial()) && sub.keyCode" class="plus-prime-keycode">
          <li>{{ t("Brand.webrootSecureAnywhereAntivirus") }}</li>
          <li>{{ t("SubscriptionInformationComponent.keycode") }} {{ sub.displayKeycode[0] }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useRouter } from "vue-router";
import { defineComponent, PropType } from "vue";
import { logEvent, logException } from "@/common/logger";
import { FLAGS } from "@/define";
import { t } from "@/i18n";
import { unifiedApi } from "@/common";
import { ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { KeyCodeSeller } from "@/globalEnums";
import { useNotificationsStore } from "@/stores/notifications";
import { carboniteProducts } from "@/common/productsBySku";
import {
  isSafeSubscriptionClosed,
  showWarning,
  showDanger,
  isProcessingRenewal,
  isNotActiveYet,
  isExpiredAllstateWithNewSubscription,
} from "@/common/subscriptionHelper";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import { webrootLicenseCategories } from "@/common/webrootProductName";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { useUserStore } from "@/stores/user";
import { URLS_JA } from "@/define";

const componentName = "ProductColumnComponent";

export default defineComponent({
  name: componentName,
  components: {
    SvgIconComponent,
  },
  props: {
    sub: {
      type: Object as PropType<ISubscription>,
      required: true,
    },
  },
  emits: ["buy-safe-sub"],

  setup(props, context) {
    logEvent("created", componentName);
    const notificationsStore = useNotificationsStore();
    const subscriptionsStore = useSubscriptionsStore();
    const buyFlowStore = useBuyFlowStore();
    const router = useRouter();
    const userStore = useUserStore();

    function showBuyFlow() {
      return FLAGS.ENABLE_BUYFLOW === "true";
    }

    function upgradeSafeSubscription(sub: ISubscription) {
      if (showBuyFlow()) {
        redirectToBuyFlow(sub);
        return;
      }

      // find the next SKU, if any, in the upgrade order for this sub.
      // if we find one, supply it in the redirect to the Buy Flow so that it'll be auto-selected.
      const upgradeSku = getUpgradeSku(props.sub);

      context.emit("buy-safe-sub", props.sub, upgradeSku);
    }

    async function upgradeWebrootSubscription() {
      // TODO: Redirect to new buyflow when we add Webroot
      notificationsStore.clearNotifications();
      subscriptionsStore.forceRefresh();
      try {
        if (props.sub.keyCode) {
          let url;
          if (userStore.selectedLanguage === "ja") {
            url = URLS_JA["WEBROOT_HOME_RENEWAL"];
          } else {
            url = (await unifiedApi.getWebrootRenewUrl(props.sub.keyCode)).data.redirectUrl;
          }
          window.open(url, "_blank");
        }
      } catch (err) {
        notificationsStore.addNotification({ type: "ServerErrors" });
        logException(err as Error);
      }
    }

    function computerName() {
      return props.sub.computer?.description ?? props.sub.computer?.name;
    }

    function isOnlyKeycode() {
      return (
        props.sub.keyCode &&
        props.sub.sku &&
        carboniteProducts.filter(product => product.sku === props.sub.sku).length === 0
      );
    }

    function showSafeUpgradeLink(): boolean {
      const upgradeSku = getUpgradeSku(props.sub);
      const isUpgradableSku = !!upgradeSku;
      let showLink = false;

      if (
        (!props.sub.resellerId || props.sub.isPartnerReferral) &&
        !props.sub.isTrial &&
        isUpgradableSku &&
        props.sub.computer &&
        props.sub.computer.id > 0 &&
        !props.sub.isSafeLite
      ) {
        if (props.sub.isEvergreenMonthly) {
          showLink = !props.sub.isCancelledEvergreenSubscription && isSafeSubscriptionClosed(props.sub);
        } else {
          showLink = !isSafeSubscriptionClosed(props.sub);
        }
      }

      return showLink;
    }

    function showWebrootUpgradeLink(): boolean {
      if (isExpiredAllstateWithNewSubscription(props.sub)) return false;

      return (
        props.sub.keyCodeSellerType === KeyCodeSeller.Webroot &&
        props.sub.sku !== webrootLicenseCategories.WSCB && // remove Upgrade link from Chromebook (WSCB)
        !isNotActiveYet(props.sub)
      );
    }

    function isSafePlusOrPrime() {
      return (
        props.sub.sku &&
        carboniteProducts
          .filter(product => product.sku !== "PersonalBasic")
          .some(product => product.sku === props.sub.sku)
      );
    }

    function showKeycodeForTrial(): boolean {
      return props.sub.isTrial && !!props.sub.keyCode && props.sub.keyCodeSellerType === KeyCodeSeller.Safe;
    }

    /**
     * Gets the SKU of the product that is one tier higher than the provided subscription's SKU
     */
    function getUpgradeSku(subscription: ISubscription): string | undefined {
      let upgradeSku: string | undefined = undefined;

      const currentSku = carboniteProducts.find(product => product.sku === subscription?.sku);

      if (!!currentSku) {
        const nextProduct = carboniteProducts.find(product => product.upgradeOrder === currentSku.upgradeOrder + 1);

        upgradeSku = nextProduct?.sku;
      }

      return upgradeSku;
    }

    async function redirectToBuyFlow(sub: ISubscription) {
      if (sub.sku && sub.subscriptionMonths) {
        buyFlowStore.changingFromMonthly = sub.isEvergreenMonthly;
        if (sub.computer?.id) {
          router.push({
            name: "selectPlan",
            query: {
              deviceId: sub.computer?.id,
              ratePlanId: sub.ratePlanId,
              subscriptionNumber: sub.zuoraSubscriptionNumber,
            },
          });
        } else {
          router.push({
            name: "selectPlan",
            query: {
              sku: sub.sku,
              ratePlanId: sub.ratePlanId,
              deviceId: sub.computer?.id,
              subscriptionNumber: sub.zuoraSubscriptionNumber,
            },
          });
        }
      }
    }

    //Expose to template
    return {
      t,
      showWarning,
      showDanger,
      isProcessingRenewal,
      computerName,
      showKeycodeForTrial,
      isSafePlusOrPrime,
      showWebrootUpgradeLink,
      showSafeUpgradeLink,
      isOnlyKeycode,
      upgradeSafeSubscription,
      upgradeWebrootSubscription,
      showBuyFlow,
      redirectToBuyFlow,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";

.icon-name-wrapper {
  display: flex;
  align-content: flex-start;
}
.icon-name-wrapper div li {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.icon {
  margin-right: var(--space-quarter);
  height: var(--icon-sm);
  width: var(--icon-sm);
}
.plus-prime-keycode {
  margin-top: var(--space);
}
@media (min-width: 600px) {
  td {
    width: 50%;
  }
}
</style>
